import React, { useCallback, useMemo } from 'react';
import { useApplication } from '../../../../hooks/useApplication';
import { useOnboardingTracking } from '../../../../hooks/useOnboardingTracking';
import { FieldObject } from '../../../../openapi/atlantis';
import { useAppSelector } from '../../../../slices/store';
import Logger from '../../../../utils/Logger';
import DefaultLoader from '../../../DefaultLoader';
import SignatureComponentV2 from './SignatureComponentV2';

interface TeamAddendumSignatureWrapperProps {
  name: string;
  field: FieldObject;
  documentSignedCallback?: () => void;
  browserMode: boolean;
}

/**
 * Wrapper component that fetches team invitation ID and renders SignatureComponentV2
 * This component is used for Team Addendum Agreement signatures in RBO flow
 */
const TeamAddendumSignatureWrapper: React.FC<
  TeamAddendumSignatureWrapperProps
> = ({ name, field, documentSignedCallback, browserMode }) => {
  const authUserDetail = useAppSelector((state) => state.auth.authUserDetail);
  const { trackSelfServeOnboardingTeamAddendumSigned } =
    useOnboardingTracking();

  const legacyApplicationId = authUserDetail?.onboardings?.find(
    (item) => item.legacyApplicationId,
  )?.legacyApplicationId;

  // Use the useApplication hook to fetch application data
  const {
    data: applicationData,
    isLoading,
    error,
  } = useApplication(legacyApplicationId);

  // Extract invitation ID from application data
  const invitationId = useMemo(() => {
    if (!applicationData?.teamInvitation) return null;
    return (
      applicationData.teamInvitation.invitationId ||
      applicationData.teamInvitation.genericTeamInvitation ||
      null
    );
  }, [applicationData]);

  // Extract team ID from application data
  const teamId = useMemo(() => {
    return applicationData?.teamInvitation?.teamId || null;
  }, [applicationData]);

  // Wrapped callback to track team addendum signature
  const wrappedDocumentSignedCallback = useCallback(
    (isSignedAlready: boolean) => {
      // Track the signature event if invitation data is available
      if (invitationId && teamId && !isSignedAlready) {
        try {
          trackSelfServeOnboardingTeamAddendumSigned(
            'team_addendum_signature_screen',
            {
              team_invite_id:
                applicationData?.teamInvitation?.genericTeamInvitation ??
                applicationData?.teamInvitation?.invitationId!,
              team_id: teamId,
              invite_method: applicationData?.teamInvitation
                ?.genericTeamInvitation
                ? 'link'
                : 'email',
            },
            'team member', // Default to team member role
            'Sign Agreement',
          );
        } catch (error) {
          Logger.error('Failed to track team addendum signed event', error);
        }
      }

      // Call the original callback if provided
      if (documentSignedCallback) {
        documentSignedCallback();
      }
    },
    [
      invitationId,
      teamId,
      documentSignedCallback,
      trackSelfServeOnboardingTeamAddendumSigned,
      applicationData?.teamInvitation?.genericTeamInvitation,
      applicationData?.teamInvitation?.invitationId,
    ],
  );

  // Create a modified field with the invitation ID
  const modifiedField = useMemo(
    () => ({
      ...field,
      invitationId,
    }),
    [field, invitationId],
  );

  if (isLoading) {
    return <DefaultLoader />;
  }

  if (error || !invitationId) {
    return (
      <div className='flex items-center justify-center w-full h-full p-6'>
        <div className='w-full max-w-md p-6 bg-gray-50 border border-gray-200 rounded-lg'>
          <p className='font-inter text-sm text-gray-700 text-center'>
            {error || 'Unable to load team agreement information.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SignatureComponentV2
      name={name}
      field={modifiedField}
      documentSignedCallback={wrappedDocumentSignedCallback}
      browserMode={browserMode}
    />
  );
};

export default TeamAddendumSignatureWrapper;
